import { useCallback, useContext } from 'react'
import useRootSnackbar from '../services/useRootSnackbar'
import useUserContextRef from '../services/useUserContextRef'
import { isMuseAudioShareInputDevice, isSystemAudio } from '../services/MediaDevices'
import ElectronIPC from '../services/ElectronIPC'
import { UserContext, UserContextType } from '../UserContext'
import { logger } from '../services/Logger'

const log = logger.createChildLogger({ name: 'GlobalOutputDeviceActions' })

export default function useGlobalOutputDeviceAction() {
    const RootSnackbar = useRootSnackbar()

    const { dispatch } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const revertToPreviousGlobalAudioOutputDevice = useCallback(() => {
        const scope = `revertToPreviousGlobalAudioOutputDevice`

        const deviceToChangeTo = userStateRef.current.mediaSelection.lastGlobalOutputDeviceBeforeAutoSelection
        log.isVerbose() &&
            log.verbose(
                scope,
                `detected selectedMuseAudioShareCt has changed to 0. we now attempt to ` +
                    `revert the global audio output device to the previously selected device. the device ` +
                    `to change to is userState.mediaSelection.lastGlobalOutputDeviceBeforeAutoSelection=` +
                    JSON.stringify(deviceToChangeTo) +
                    '.'
            )

        // EDGE CASE: what happens if currently selected global audio output device is NOT a
        // Muse Audio Share device?
        // get current global output device
        const globalOutputDevice = userStateRef.current.mediaSelection.globalOutputDevice
        if (globalOutputDevice) {
            // the deviceId is there to just fit the required type.
            if (!isMuseAudioShareInputDevice({ ...globalOutputDevice, deviceId: '' })) {
                log.isVerbose() &&
                    log.verbose(
                        scope,
                        `global audio output device corresponding to Muse Audio Share ` + `not selected; no-op.`
                    )
                return
            }
        }

        const change = (nativeDeviceId: string, label: string) => {
            ElectronIPC.setGlobalAudioOuputDevice({ nativeDeviceId })

            dispatch({
                type: 'update_media_selection',
                value: {
                    lastGlobalOutputDeviceBeforeAutoSelection: null,
                },
            })

            RootSnackbar.show({
                severity: 'success',
                message: `Stopped sharing computer audio. Computer audio changed back to ${label}.`,
            })
        }

        if (deviceToChangeTo?.nativeDeviceId) {
            const nativeDeviceId = deviceToChangeTo.nativeDeviceId as any
            log.verbose(
                scope,
                `calling ElectronIPC.setGlobalAudioOuputDevice() on ` + `nativeDeviceId="${nativeDeviceId}".`
            )

            change(nativeDeviceId, deviceToChangeTo.label)
        } else {
            const globalOutputDeviceHistory = userStateRef.current.mediaSelection.globalOutputDeviceHistory || []

            log.verbose(
                scope,
                `deviceToChangeTo?.nativeDeviceId=${deviceToChangeTo?.nativeDeviceId} is ` +
                    `null; we look at the globalOutputDeviceHistory.`,
                { globalOutputDeviceHistory }
            )

            let fallbackDevice = globalOutputDeviceHistory[globalOutputDeviceHistory.length - 2] || null
            for (let i = globalOutputDeviceHistory.length - 1; i >= 0; --i) {
                let d = globalOutputDeviceHistory[i]
                if (!isSystemAudio(d)) {
                    fallbackDevice = d
                    break
                }
            }

            if (fallbackDevice?.nativeDeviceId) {
                log.verbose(
                    scope,
                    `calling ElectronIPC.setGlobalAudioOuputDevice() on ` +
                        `nativeDeviceId="${fallbackDevice?.nativeDeviceId}".`
                )

                change(fallbackDevice.nativeDeviceId, fallbackDevice.label)
            } else {
                log.verbose(scope, `no global output device to change to; no-op.`)
            }
        }
    }, [])

    return {
        revertToPreviousGlobalAudioOutputDevice,
    }
}
